import { MemberProfile } from 'bff/models/member';
import commonService from 'utils/commonService';
import apiService from './apiService';
import { LinkCategory } from 'constants/enums/linkCategory.enum';
import { BaseRes } from 'bff/models/base/baseRes';
import { Links, Regdoc } from 'bff/models/link';
import { Secques, Nationalitys, Occupations, Postcodes, EmailDomains, Codes, Activities, Activity } from 'bff/models/info';
import { Signup } from 'constants/models/components/signup';
import { ReturnCode } from 'constants/enums/returnCode.enum';
import { CodeClass } from 'constants/enums/codeClass.enum';
import { MemberPartnerReq, MemberPartnerResp } from 'bff/models/member/memberPartner';

/**
 * @description 會員註冊頁初始化資料設定方法
 */
const fetchInitSetting = async (applicationId?: string):Promise<Signup> => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig(applicationId);

  const result = await Promise.all([
    apiService.fetchSecque(baseReq, reqConfig),
    apiService.fetchNationality(baseReq, reqConfig),
    apiService.fetchOccupation(baseReq, reqConfig),
    apiService.fetchPostcode(baseReq, reqConfig),
    apiService.fetchEmailDN(baseReq, reqConfig),
    apiService.fetchCodes(baseReq, reqConfig, CodeClass.UI),
    apiService.fetchActivities(baseReq, reqConfig)
  ]).then(([secquesRes, nationalitysRes, occupationsRes, postcodesRes, emailDomainsRes, UICodeRes, activitiesRes]) => {
    const secquesBaseRes:BaseRes<Secques> = { ...secquesRes };
    const nationalitysBaseRes:BaseRes<Nationalitys> = { ...nationalitysRes };
    const occupationsBaseRes:BaseRes<Occupations> = { ...occupationsRes };
    const postcodesBaseRes:BaseRes<Postcodes> = { ...postcodesRes };
    const emailDomainsBaseRes:BaseRes<EmailDomains> = { ...emailDomainsRes };
    const UICodeBaseRes:BaseRes<Codes> = { ...UICodeRes };
    const activitiesBaseRes:BaseRes<Activities> = { ...activitiesRes };

    const res:Signup = {
      secques: commonService.arrayTypeReturn(secquesBaseRes.TRANRS.SECQUE),
      nationalitys: commonService.arrayTypeReturn(nationalitysBaseRes.TRANRS.NATIONALITY),
      occupations: commonService.filterOccupationOpts(commonService.arrayTypeReturn(occupationsBaseRes.TRANRS.OCCUPATION)),
      postcodes: commonService.arrayTypeReturn(postcodesBaseRes.TRANRS.POSTCODE),
      emailDomains: commonService.arrayTypeReturn(emailDomainsBaseRes.TRANRS.EMAILDN),
      UICode: commonService.arrayTypeReturn(UICodeBaseRes.TRANRS.CODES),
      activities: {
        MORE_ACTIVITY_URL: activitiesBaseRes.TRANRS.MORE_ACTIVITY_URL ? activitiesBaseRes.TRANRS.MORE_ACTIVITY_URL : '',
        ACTIVITY: activitiesBaseRes.TRANRS.ACTIVITY ? [...activitiesBaseRes.TRANRS.ACTIVITY.sort((first:Activity, second:Activity) => +first.SHOW_ORDER - +second.SHOW_ORDER)] : new Array(0)
      }
    };

    return res;
  });

  return result;
};

/**
 * @description 取得會員註冊條款頁條款資料
*/
const fetchRegdocLinks = async (applicationId?: string) => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig(applicationId);
  const res = await apiService.fetchLinkByCategory(baseReq, reqConfig, LinkCategory.Regdoc).then((res:BaseRes<Links<Regdoc>>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return commonService.arrayTypeReturn(res.TRANRS.LINKS?.REGDOC);
  }
};

/**
 * @description 會員註冊 新增會員基本資料
 * @param memberProfile 會員基本資料
*/
const insertMemberProfile = async (memberProfile:MemberProfile, applicationId?: string) => {
  const baseReq = commonService.getApiReqData({ MEMBER: memberProfile });
  const reqConfig = commonService.getReqConfig(applicationId);

  // console.log('insert baseReq', baseReq);

  const res = await apiService.insertMemberProfile(baseReq, reqConfig).then((res:BaseRes<any>) => res);

  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res.TRANRS;
  }
};

/**
 * @description 檢查外部網頁應用系統代號
 * @param args 網址所帶的系統代號
 */
const checkApplicationId = async (args: MemberPartnerReq) => {
  const baseReq = commonService.getApiReqData(args);
  const reqConfig = commonService.getReqConfig();
  const res: BaseRes<any> = await apiService.checkMemberPartner(baseReq, reqConfig);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    return undefined; // 不需跳出錯誤訊息
  } else {
    return res.TRANRS as MemberPartnerResp;
  }
};

export default {
  fetchInitSetting,
  fetchRegdocLinks,
  insertMemberProfile,
  checkApplicationId
};
